<template lang="pug">
.error(v-if="!checkRestrictions('ex:shop')")
    Error(:message="null" :code="403")
section.abonement(v-else)
    //- The store has the shop extension - that is why this tab exists - but no
    //- shop yet. Choosing a parent is the only thing that can be done here, so it
    //- IS the screen until one is chosen.
    .shop-attach(v-if="shop && !form.shop")
        el-form-item(:label="$t('Existing shop')")
            el-select.is-full-width(
                v-model="parentShopId"
                :placeholder="$t('Choose a shop')"
                :loading="loadingAttachableShops"
                filterable
            )
                el-option(
                    v-for="parentShop in attachableShops"
                    :key="parentShop.id"
                    :label="parentShop.name"
                    :value="parentShop.id"
                )
            .help {{ $t('Catalog, stock, prices and customer groups come from this shop. Shipping methods, payment methods and the communication setup are copied from its main store and can be adjusted afterwards.') }}

    .shop(v-if="shop && form.shop")
        el-row.no-margin(:gutter="20")
            el-col(:sm="12")
                //- Multistore: the same fields write the shop on the owner store and
                //- this store's own override on a satellite, so say which it is.
                .shared-scope(v-if="shopIdentityIsShared")
                    span.shared-scope__hint {{ $t('Every store of this shop issues its paperwork under this identity, unless it overrides a field.') }}
                .shared-scope(v-else-if="writesStoreOverride")
                    span.shared-scope__hint {{ $t('This store only. Leave a field empty to keep using the shop\'s data.') }}

                el-form-item(:label="$t('Shop name')" :prop="identityProp('name', 'company_name')")
                    el-input(v-model="shop_name" :placeholder="shopIdentityFallback('name')" maxlength="255" show-word-limit)
                .block
                    h4 {{ $t('Contact details') }}
                    el-form-item(:label="$t('Email')" :prop="identityProp('email', 'email')")
                        el-input(v-model="shop_email" :placeholder="shopIdentityFallback('email')" type="email" maxlength="255" show-word-limit)
                    el-form-item(:label="$t('Phone')" :prop="identityProp('phone', 'phone')")
                        el-input(v-model="shop_phone" :placeholder="shopIdentityFallback('phone')" maxlength="255" show-word-limit)
                    el-form-item(:label="$t('Fax')" :prop="identityProp('fax', 'fax')")
                        el-input(v-model="shop_fax" :placeholder="shopIdentityFallback('fax')" maxlength="255" show-word-limit)
                .block
                    h4 {{ $t('Address') }}
                    template(v-if="form.shop && form.shop.address && !form.shop.address.deleted")
                        .card
                            el-form-item(:label="$t('Street')" :prop="identityProp('address.street', 'address.street')")
                                el-input(v-model="shop_address_street" :placeholder="shopIdentityFallback('address.street')" maxlength="255" show-word-limit)
                            el-row.no-padding(:gutter="20")
                                el-col(:lg="8" :md="8")
                                    el-form-item(:label="$t('Zipcode')" :prop="identityProp('address.zipcode', 'address.zipcode')")
                                        el-input(v-model="shop_address_zipcode" :placeholder="shopIdentityFallback('address.zipcode')" maxlength="255" show-word-limit)
                                el-col(:lg="16" :md="16")
                                    el-form-item(:label="$t('City')" :prop="identityProp('address.city', 'address.city')")
                                        el-input(v-model="shop_address_city" :placeholder="shopIdentityFallback('address.city')" maxlength="255" show-word-limit)
                            el-form-item(:label="$t('Country')" :prop="identityProp('address.country', 'address.country')")
                                el-select.is-full-width(:placeholder="shopAddressCountryPlaceholder" filterable v-model="shop_address_country" autocomplete="no-fill")
                                    el-option(v-for="(label, value) in countries" :key="value" :label="label" :value="value")
                        el-button(type="warning" size="mini" icon="el-icon-fa-trash-alt fas" @click="removeShopAddress") {{ $t('Remove individual shop address') }}
                    template(v-else)
                        address-card.address(
                            v-if="address"
                            :key="address.id"
                            :address="address"
                            @set-marker="({ latitude, longitude }) => setMarker(address, latitude, longitude)"
                        )
                            template(slot="title")
                                h3 {{ $t('Default account address') }}
                        el-button(type="info" size="mini" icon="el-icon-fa-edit fas" @click="addShopAddress") {{ $t('Define individual shop address') }}
                .block
                    h4 {{ $t('Bank details') }}
                    el-form-item(:label="$t('Bank name')" :prop="identityProp('bank_name', 'bank_name')")
                        el-input(v-model="shop_bank_name" :placeholder="shopIdentityFallback('bank_name')" maxlength="255" show-word-limit)
                    el-form-item(:label="$t('IBAN')" :prop="identityProp('iban', 'iban')")
                        el-input(v-model="shop_iban" :placeholder="shopIdentityFallback('iban')" maxlength="255" show-word-limit)
                    el-form-item(:label="$t('BIC')" :prop="identityProp('bic', 'bic')")
                        el-input(v-model="shop_bic" :placeholder="shopIdentityFallback('bic')" maxlength="255" show-word-limit)
                .block
                    h4 {{ $t('Tax details') }}
                    el-form-item(:label="$t('Tax ID')" :prop="identityProp('tax_id', 'tax_id')")
                        el-input(v-model="shop_tax_id" :placeholder="shopIdentityFallback('tax_id')" maxlength="255" show-word-limit)
                    el-form-item(:label="$t('Tax nr')" :prop="identityProp('tax_nr', 'tax_nr')")
                        el-input(v-model="shop_tax_nr" :placeholder="shopIdentityFallback('tax_nr')" maxlength="255" show-word-limit)
            el-col(:sm="12")
                el-form-item(:label="$t('Shop ID')" prop="shop.id")
                    el-input(:value="form.shop ? form.shop.id : ''" :disabled="true")

                .groups
                    transition-group(name="slide-decent-vertical")
                        .row(
                            v-for="(group, i) in guestMarketGroups" :key="'gm-' + i"
                            v-if="checkConditions(group)"
                        )
                            transition-group.flex(name="slide-decent-vertical")
                                SettingField.setting-field(
                                    v-for="field in group.fields"
                                    v-if="checkConditions(field)"
                                    :key="field.key"
                                    :field="field"
                                    :setting-values="settingValues"
                                )
                                MicroserviceObjectForm(
                                    v-for="object in group.objects || []"
                                    v-if="checkConditions(object)"
                                    :key="'object-' + object.key"
                                    :config="object"
                                    :cms-id="form.id"
                                    :setting-values="settingValues"
                                )

                .guest-market-rules-block(v-if="alternativeGuestCustomerGroupsFeatureEnabled")
                    guest-market-rules

                .groups
                    transition-group(name="slide-decent-vertical")
                        .row(
                            v-for="(group, i) in otherGroups" :key="'other-' + i"
                            v-if="checkConditions(group)"
                        )
                            transition-group.flex(name="slide-decent-vertical")
                                SettingField.setting-field(
                                    v-for="field in group.fields"
                                    v-if="checkConditions(field)"
                                    :key="field.key"
                                    :field="field"
                                    :setting-values="settingValues"
                                )
                                MicroserviceObjectForm(
                                    v-for="object in group.objects || []"
                                    v-if="checkConditions(object)"
                                    :key="'object-' + object.key"
                                    :config="object"
                                    :cms-id="form.id"
                                    :setting-values="settingValues"
                                )

                el-collapse(accordion)
                    el-collapse-item(:title="$t('Invoice')" name="_invoice")
                        .invoice-template-guide.alert.info
                            h2 {{ $t('Invoice Number Template Guide') }}
                            p {{ $t('You can customize your invoice numbers by using a template with variables inside double curly braces.') }}
                            el-button(
                                v-if="!showInvoiceTemplateGuide"
                                type="text"
                                @click="showInvoiceTemplateGuide = !showInvoiceTemplateGuide"
                            ) {{ $t('show more...') }}
                            transition(name="slide-fade" mode="out-in")
                                .invoice-template-guide-content(v-if="showInvoiceTemplateGuide")
                                    p {{ $t('Here are the available options:') }}
                                    h3 {{ $t('Variables') }}
                                    ul.no-style-list
                                        li
                                            code(v-pre) {{ ID }}
                                            | &nbsp; {{ $t('Order ID') }}
                                        li
                                            code(v-pre) {{ uniq }}
                                            | &nbsp; {{ $t('A random, unique combination of letters and numbers') }}
                                        li
                                            code(v-pre) {{ Y }}
                                            | &nbsp; {{ $t('Year (4 digits, e.g., 2024)') }}
                                        li
                                            code(v-pre) {{ y }}
                                            | &nbsp; {{ $t('Year (2 digits, e.g., 24)') }}
                                        li
                                            code(v-pre) {{ M }}
                                            | &nbsp; {{ $t('Month (2 digits, e.g., 09 for September)') }}
                                        li
                                            code(v-pre) {{ m }}
                                            | &nbsp; {{ $t('Month (1 or 2 digits, e.g., 9 for September or 10 for October)') }}
                                        li
                                            code(v-pre) {{ D }}
                                            | &nbsp; {{ $t('Day (2 digits, e.g., 07 for the 7th)') }}
                                        li
                                            code(v-pre) {{ d }}
                                            | &nbsp; {{ $t('Day (1 or 2 digits, e.g., 7 for the 7th)') }}
                                        li
                                            code(v-pre) {{ incr }}
                                            | &nbsp; {{ $t('Incremental number that increases with each new invoice') }}
                                        li
                                            code(v-pre) {{ incry }}
                                            | &nbsp; {{ $t('Incremental number that resets every year') }}
                                        li
                                            code(v-pre) {{ incrm }}
                                            | &nbsp; {{ $t('Incremental number that resets every month') }}
                                        template(v-if="isMultistore")
                                            li
                                                code(v-pre) {{ incr_shop }}
                                                | &nbsp; {{ $t('Incremental number continuing across all stores of the shared shop') }}
                                            li
                                                code(v-pre) {{ incry_shop }}
                                                | &nbsp; {{ $t('Shop-wide incremental number that resets every year') }}
                                            li
                                                code(v-pre) {{ incrm_shop }}
                                                | &nbsp; {{ $t('Shop-wide incremental number that resets every month') }}

                                    h3 {{ $t('Formatting (Modifiers/Filters)') }}
                                    p {{ $t('You can add leading zeros to make the number a fixed length. For example:') }}
                                    ul.no-style-list
                                        li
                                            code(v-pre) {{ incr | strpad(6, &apos;0&apos;) }}
                                            | &nbsp; {{ $t('This will add leading zeros to make "incr" 6 digits long (e.g., 000123)') }}
                                    h3 {{ $t('Example') }}
                                    p {{ $t('To create an invoice number like "{example}", you can use this template:', { example: invoiceNrExample }) }}
                                    code {{ invoiceNrExampleTemplate }}
                                    p {{ $t('This would give you "{example}" if the year is {year}, the month is {month}, and the incremental number is {number}.', { example: invoiceNrExample, year: exampleYear, month: exampleMonthName, number: invoiceNrExampleNumber }) }}
                                    br
                                    p {{ $t('Feel free to adjust the template to match your preferences!') }}

                        transition(name="slide-decent-vertical")
                            .invoice-template-preview.alert.primary(v-if="invoiceNrPreview")
                                h2 {{ $t('Invoice Number Preview') }}
                                code {{ invoiceNrPreview }}

                        .flex-row
                            el-form-item.grow(:label="$t('Invoice Number Template')" prop="invoice_nr_template")
                                el-input.code-font(
                                    v-model="invoice_nr_template"
                                    :spellcheck="false"
                                    :placeholder="'{{ ID }}'"
                                )
                            el-form-item(:label="$t('Reset incremental numbers at')")
                                el-date-picker.is-full-width(
                                    v-model="resetIncrementNumbersAt"
                                    type="datetime"
                                    :format="$t('yyyy-MM-dd') + ' HH:mm'"
                                    prefix-icon="far el-icon-fa-calendar-alt"
                                    :unlink-panels="true"
                                    :clearable="true"
                                    :picker-options="{ firstDayOfWeek: 1, disabledDate }"
                                    @input="generateInvoiceNrPreview"
                                )
                        transition(name="slide-decent-vertical")
                            .flex-row(v-if="resetIncrementNumbersAt")
                                el-form-item.grow(:label="$t('Absolute Incremental Number')" prop="new_increment_all_time")
                                    el-input(
                                        v-model="new_increment_all_time"
                                        type="number"
                                        :step="1"
                                        :step-strictly="true"
                                        :placeholder="numberingPlaceholder('all_time')"
                                        @input="generateInvoiceNrPreview"
                                    )
                                el-form-item.grow(:label="$t('Yearly Incremental Number')" prop="new_increment_yearly")
                                    el-input(
                                        v-model="new_increment_yearly"
                                        type="number"
                                        :step="1"
                                        :step-strictly="true"
                                        :placeholder="numberingPlaceholder('yearly')"
                                        @input="generateInvoiceNrPreview"
                                    )
                                el-form-item.grow(:label="$t('Monthly Incremental Number')" prop="new_increment_monthly")
                                    el-input(
                                        v-model="new_increment_monthly"
                                        type="number"
                                        :step="1"
                                        :step-strictly="true"
                                        :placeholder="numberingPlaceholder('monthly')"
                                        @input="generateInvoiceNrPreview"
                                    )

                    el-collapse-item(:title="$t('Customer')" name="_customer")
                        .shared-scope(v-if="isMultistoreShop")
                            span.shared-scope__hint {{ $t('Applies to every store of this shop.') }}
                        .flex-row
                            el-form-item
                                el-switch(
                                    :active-text="$t('Show net prices to new customers')"
                                    v-model="newCustomersShowNetPrices"
                                )
                            el-form-item
                                el-switch(
                                    :active-text="$t('Tax-free for new customers')"
                                    v-model="newCustomersTaxFree"
                                )

                        .shared-scope(v-if="isMultistoreShop")
                            span.shared-scope__hint {{ $t('The customer number below is this store\'s own.') }}
                        .customer-template-guide.alert.info
                            h2 {{ $t('Customer Number Template Guide') }}
                            p {{ $t('You can customize your customer numbers by using a template with variables inside double curly braces.') }}
                            el-button(
                                v-if="!showCustomerTemplateGuide"
                                type="text"
                                @click="showCustomerTemplateGuide = !showCustomerTemplateGuide"
                            ) {{ $t('show more...') }}
                            transition(name="slide-fade" mode="out-in")
                                .customer-template-guide-content(v-if="showCustomerTemplateGuide")
                                    p {{ $t('Here are the available options:') }}
                                    h3 {{ $t('Variables') }}
                                    ul.no-style-list
                                        li
                                            code(v-pre) {{ ID }}
                                            | &nbsp; {{ $t('Customer ID') }}
                                        li
                                            code(v-pre) {{ uniq }}
                                            | &nbsp; {{ $t('A random, unique combination of letters and numbers') }}
                                        li
                                            code(v-pre) {{ Y }}
                                            | &nbsp; {{ $t('Year (4 digits, e.g., 2024)') }}
                                        li
                                            code(v-pre) {{ y }}
                                            | &nbsp; {{ $t('Year (2 digits, e.g., 24)') }}
                                        li
                                            code(v-pre) {{ M }}
                                            | &nbsp; {{ $t('Month (2 digits, e.g., 09 for September)') }}
                                        li
                                            code(v-pre) {{ m }}
                                            | &nbsp; {{ $t('Month (1 or 2 digits, e.g., 9 for September or 10 for October)') }}
                                        li
                                            code(v-pre) {{ D }}
                                            | &nbsp; {{ $t('Day (2 digits, e.g., 07 for the 7th)') }}
                                        li
                                            code(v-pre) {{ d }}
                                            | &nbsp; {{ $t('Day (1 or 2 digits, e.g., 7 for the 7th)') }}
                                        li
                                            code(v-pre) {{ incr }}
                                            | &nbsp; {{ $t('Incremental number that increases with each new customer') }}
                                        li
                                            code(v-pre) {{ incry }}
                                            | &nbsp; {{ $t('Incremental number that resets every year') }}
                                        li
                                            code(v-pre) {{ incrm }}
                                            | &nbsp; {{ $t('Incremental number that resets every month') }}
                                        template(v-if="isMultistore")
                                            li
                                                code(v-pre) {{ incr_shop }}
                                                | &nbsp; {{ $t('Incremental number continuing across all stores of the shared shop') }}
                                            li
                                                code(v-pre) {{ incry_shop }}
                                                | &nbsp; {{ $t('Shop-wide incremental number that resets every year') }}
                                            li
                                                code(v-pre) {{ incrm_shop }}
                                                | &nbsp; {{ $t('Shop-wide incremental number that resets every month') }}

                                    h3 {{ $t('Formatting (Modifiers/Filters)') }}
                                    p {{ $t('You can add leading zeros to make the number a fixed length. For example:') }}
                                    ul.no-style-list
                                        li
                                            code(v-pre) {{ incr | strpad(6, &apos;0&apos;) }}
                                            | &nbsp; {{ $t('This will add leading zeros to make "incr" 6 digits long (e.g., 000123)') }}
                                    h3 {{ $t('Example') }}
                                    p {{ $t('To create a customer number like "{example}", you can use this template:', { example: customerNrExample }) }}
                                    code {{ customerNrExampleTemplate }}
                                    p {{ $t('This would give you "{example}" if the year is {year}, the month is {month}, and the incremental number is {number}.', { example: customerNrExample, year: exampleYear, month: exampleMonthName, number: customerNrExampleNumber }) }}
                                    br
                                    p {{ $t('Feel free to adjust the template to match your preferences!') }}

                        transition(name="slide-decent-vertical")
                            .customer-template-preview.alert.primary(v-if="customerNrPreview")
                                h2 {{ $t('Customer Number Preview') }}
                                code {{ customerNrPreview }}

                        .flex-row
                            el-form-item.grow(:label="$t('Customer Number Template')" prop="customer_nr_template")
                                el-input.code-font(
                                    v-model="customer_nr_template"
                                    :spellcheck="false"
                                    :placeholder="'{{ ID }}'"
                                )

                    //- Shop-wide and owner-only in the API, so a satellite never sees it.
                    //- docs/features/PURCHASE_OPERATOR_NOTIFICATIONS.md
                    el-collapse-item(v-if="!writesStoreOverride" :title="$t('Order notifications')" name="_order_notifications")
                        .shared-scope(v-if="isMultistoreShop")
                            span.shared-scope__hint {{ $t('Applies to every store of this shop.') }}
                        el-form-item
                            el-switch(
                                :active-text="$t('Email me about every new order')"
                                v-model="newPurchaseNotificationEnabled"
                            )
                            .help {{ $t('Also for orders whose customer is not emailed, such as marketplace orders.') }}
                        el-form-item(:label="$t('Recipient')")
                            el-input(
                                v-model="purchaseNotificationRecipientEmail"
                                :placeholder="purchaseNotificationRecipientFallback"
                                type="email"
                                maxlength="255"
                                show-word-limit
                            )
                            .help {{ $t('Leave empty to use the shop email address or, without one, the account owner.') }}
                        el-form-item
                            el-switch(
                                :active-text="$t('Send me a blind copy of every customer email')"
                                v-model="purchaseMailCopiesEnabled"
                            )
                            .help {{ $t('Order confirmations, order changes and shipping notices go to the recipient above, invoices to the invoice recipient.') }}

                    el-collapse-item(:title="$t('Returns')" name="_returns")
                        .shared-scope(v-if="isMultistoreShop")
                            span.shared-scope__hint {{ $t('Applies to every store of this shop.') }}
                        .returns-grid
                            el-form-item.returns-field(:label="$t('Revocation window (days)')")
                                el-input-number.is-full-width(
                                    v-model="revocationWindowDays"
                                    :min="14"
                                    :max="365"
                                )
                                .help {{ $t('Minimum 14 days per § 355 BGB.') }}
                            el-form-item.returns-field(:label="$t('Delivery transit days')")
                                el-input-number.is-full-width(
                                    v-model="deliveryTransitDays"
                                    :min="0"
                                    :max="14"
                                )
                                .help {{ $t('Added to ship date to derive presumed receipt (§ 356 BGB).') }}
                            el-form-item.returns-field(:label="$t('Return grace period (days)')")
                                el-input-number.is-full-width(
                                    v-model="returnGracePeriodDays"
                                    :min="14"
                                    :max="90"
                                )
                                .help {{ $t('Time after withdrawal until items must arrive (§ 357 BGB).') }}
                            el-form-item.returns-field(:label="$t('Return reminder lead time (days)')")
                                el-input-number.is-full-width(
                                    v-model="returnReminderDaysBefore"
                                    :min="0"
                                    :max="89"
                                )
                                .help {{ $t('0 disables the reminder email.') }}
                        .return-reasons-block(:id="returnReasonsAnchorId")
                            ReturnReasons

                    transition-group(v-if="blocks && blocks.length" name="slide-decent-vertical")
                        el-collapse-item(
                            v-for="block in blocks" :key="block.key"
                            v-if="checkConditions(block)"
                            :title="block.name"
                            :name="block.key"
                        )
                            transition-group(name="slide-decent-vertical")
                                .row(
                                    v-for="(group, i) in block.groups" :key="i"
                                    v-if="checkConditions(group)"
                                )
                                    transition-group.flex(name="slide-decent-vertical")
                                        SettingField.setting-field.no-margin(
                                            v-for="field in group.fields"
                                            v-if="checkConditions(field)"
                                            :key="field.key"
                                            :field="field"
                                            :setting-values="settingValues"
                                        )
                                        MicroserviceObjectForm(
                                            v-for="object in group.objects || []"
                                            v-if="checkConditions(object)"
                                            :key="'object-' + object.key"
                                            :config="object"
                                            :cms-id="form.id"
                                            :setting-values="settingValues"
                                        )

</template>

<i18n>
{
    "en": {
        "To create an invoice number like \"{example}\", you can use this template:": "To create an invoice number like \"{example}\", you can use this template:",
        "To create a customer number like \"{example}\", you can use this template:": "To create a customer number like \"{example}\", you can use this template:",
        "This would give you \"{example}\" if the year is {year}, the month is {month}, and the incremental number is {number}.": "This would give you \"{example}\" if the year is {year}, the month is {month}, and the incremental number is {number}."
    },
    "de": {
        "Existing shop": "Bestehender Shop",
        "Choose a shop": "Shop wählen",
        "Catalog, stock, prices and customer groups come from this shop. Shipping methods, payment methods and the communication setup are copied from its main store and can be adjusted afterwards.": "Katalog, Lagerbestand, Preise und Kundengruppen kommen von diesem Shop. Versandmethoden, Zahlungsarten und die Kommunikationseinrichtung werden vom Hauptstore übernommen und können danach angepasst werden.",
        "Store could not be connected": "Store konnte nicht verbunden werden",
        "To create an invoice number like \"{example}\", you can use this template:": "Um eine Rechnungsnummer wie \"{example}\" zu erstellen, können Sie diese Vorlage verwenden:",
        "To create a customer number like \"{example}\", you can use this template:": "Um eine Kundennummer wie \"{example}\" zu erstellen, können Sie diese Vorlage verwenden:",
        "This would give you \"{example}\" if the year is {year}, the month is {month}, and the incremental number is {number}.": "Das würde \"{example}\" ergeben, wenn das Jahr {year} ist, der Monat {month}, und die fortlaufende Nummer {number}.",
        "Applies to every store of this shop.": "Gilt für alle Stores dieses Shops.",
        "Order notifications": "Bestellbenachrichtigungen",
        "Email me about every new order": "Bei jeder neuen Bestellung per E-Mail benachrichtigen",
        "Also for orders whose customer is not emailed, such as marketplace orders.": "Auch bei Bestellungen, deren Kunde keine E-Mail erhält, zum Beispiel Marktplatzbestellungen.",
        "Recipient": "Empfänger",
        "Leave empty to use the shop email address or, without one, the account owner.": "Leer lassen, um die E-Mail-Adresse des Shops oder, falls keine hinterlegt ist, den Kontoinhaber zu verwenden.",
        "Send me a blind copy of every customer email": "Eine Blindkopie jeder Kunden-E-Mail erhalten",
        "Order confirmations, order changes and shipping notices go to the recipient above, invoices to the invoice recipient.": "Bestellbestätigungen, Bestelländerungen und Versandbenachrichtigungen gehen an den Empfänger oben, Rechnungen an den Rechnungsempfänger.",
        "The customer number below is this store's own.": "Die Kundennummer unten gehört nur zu diesem Store.",
        "Every store of this shop issues its paperwork under this identity, unless it overrides a field.": "Alle Stores dieses Shops stellen ihre Dokumente unter dieser Identität aus, sofern sie ein Feld nicht überschreiben.",
        "This store only. Leave a field empty to keep using the shop's data.": "Nur dieser Store. Leer lassen, um die Angaben des Shops zu verwenden.",
        "Returns": "Widerruf",
        "Revocation window (days)": "Widerrufsfrist (Tage)",
        "Delivery transit days": "Versanddauer (Tage)",
        "Return grace period (days)": "Rücksende-Frist (Tage)",
        "Return reminder lead time (days)": "Erinnerungs-Vorlauf (Tage)",
        "Minimum 14 days per § 355 BGB.": "Mindestens 14 Tage gemäß § 355 BGB.",
        "Added to ship date to derive presumed receipt (§ 356 BGB).": "Wird zum Versanddatum addiert, um den Beginn der Frist zu bestimmen (§ 356 BGB).",
        "Time after withdrawal until items must arrive (§ 357 BGB).": "Frist für den Eingang der Rücksendung gemäß § 357 BGB.",
        "0 disables the reminder email.": "0 deaktiviert die Erinnerungs-E-Mail.",
        "Shop name": "Shopname",
        "Email": "Email",
        "Phone": "Telefon",
        "Fax": "Fax",
        "Address": "Adresse",
        "Default account address": "Standard Account-Adresse",
        "Define individual shop address": "Individuelle Shopadresse definieren",
        "Remove individual shop address": "Individuelle Shopadresse entfernen",
        "Street": "Straße",
        "Zipcode": "PLZ",
        "City": "Ort",
        "Country": "Land",
        "Contact details": "Kontaktdaten",
        "Bank details": "Bankverbindung",
        "Bank name": "Kreditinstitut",
        "IBAN": "IBAN",
        "BIC": "BIC",
        "Tax details": "Steuerdaten",
        "Tax ID": "Ust-ID",
        "Tax nr": "Steuernummer",
        "Add Domain": "Domain hinzufügen",
        "Invoice": "Rechnung",
        "Customer": "Kunde",
        "Show net prices to new customers": "Nettopreise für neue Kunden anzeigen",
        "Tax-free for new customers": "Steuerfrei für neue Kunden",
        "Invoice Number Template Guide": "Anleitung zur Rechnungsnummernvorlage",
        "show more...": "mehr anzeigen...",
        "You can customize your invoice numbers by using a template with variables inside double curly braces.": "Sie können Ihre Rechnungsnummern anpassen, indem Sie eine Vorlage mit Variablen in doppelte geschweifte Klammern setzen.",
        "Here are the available options:": "Hier sind die verfügbaren Optionen:",
        "Variables": "Variablen",
        "Order ID": "Bestell-ID",
        "A random, unique combination of letters and numbers": "Eine zufällige, eindeutige Kombination aus Buchstaben und Zahlen",
        "Year (4 digits, e.g., 2024)": "Jahr (4-stellig, z.B. 2024)",
        "Year (2 digits, e.g., 24)": "Jahr (2-stellig, z.B. 24)",
        "Month (2 digits, e.g., 09 for September)": "Monat (2-stellig, z.B. 09 für September)",
        "Month (1 or 2 digits, e.g., 9 for September or 10 for October)": "Monat (1 oder 2-stellig, z.B. 9 für September oder 10 für Oktober)",
        "Day (2 digits, e.g., 07 for the 7th)": "Tag (2-stellig, z.B. 07 für den 7.)",
        "Day (1 or 2 digits, e.g., 7 for the 7th)": "Tag (1 oder 2-stellig, z.B. 7 für den 7.)",
        "Incremental number that increases with each new invoice": "Fortlaufende Nummer, die mit jeder neuen Rechnung erhöht wird",
        "Incremental number that resets every year": "Fortlaufende Nummer, die jedes Jahr wieder bei 1 beginnt",
        "Incremental number that resets every month": "Fortlaufende Nummer, die jeden Monat wieder bei 1 beginnt",
        "Incremental number continuing across all stores of the shared shop": "Fortlaufende Nummer, die über alle Stores des geteilten Shops weiterläuft",
        "Shop-wide incremental number that resets every year": "Shop-weite fortlaufende Nummer, die jedes Jahr wieder bei 1 beginnt",
        "Shop-wide incremental number that resets every month": "Shop-weite fortlaufende Nummer, die jeden Monat wieder bei 1 beginnt",
        "Formatting (Modifiers/Filters)": "Formatierung (Modifikatoren/Filter)",
        "You can add leading zeros to make the number a fixed length. For example:": "Sie können führende Nullen hinzufügen, um die Nummer auf eine feste Länge zu bringen. Zum Beispiel:",
        "This will add leading zeros to make \"incr\" 6 digits long (e.g., 000123)": "Dies fügt führende Nullen hinzu, sodass \"incr\" 6 Stellen lang wird (z.B. 000123)",
        "Example" : "Beispiel",
        "Feel free to adjust the template to match your preferences!": "Passen Sie die Vorlage gerne entsprechend Ihren Wünschen an!",
        "Invoice Number Template": "Rechnungsnummer Vorlage",
        "Invoice Number Preview": "Rechnungsnummer Vorschau",
        "Absolute Incremental Number": "Absolute fortlaufende Nummer",
        "Yearly Incremental Number": "Jährliche fortlaufende Nummer",
        "Monthly Incremental Number": "Monatliche fortlaufende Nummer",
        "Reset incremental numbers at": "Fortlaufende Nummern zurücksetzen am",

        "Customer Number Template Guide": "Anleitung zur Kundennummer Vorlage",
        "You can customize your customer numbers by using a template with variables inside double curly braces.": "Sie können Ihre Kundennummern anpassen, indem Sie eine Vorlage mit Variablen in doppelte geschweifte Klammern setzen.",
        "Customer ID": "Kunden-ID",
        "Incremental number that increases with each new customer": "Fortlaufende Nummer, die mit jedem neuen Kunden erhöht wird",
        "Customer Number Template": "Kundennummer Vorlage",
        "Customer Number Preview": "Kundennummer Vorschau"
    }
}
</i18n>

<script>

import _ from 'lodash'
import uniqid from 'uniqid'
import { format, fromUnixTime, getUnixTime, endOfDay, isPast } from 'date-fns'
import { activeDateLocale } from '@/utils/dateLocale'
import { appFromUnixTime, appGetUnixTime } from '@/utils/timezone'

import { twig, default as Twig } from 'twig'

Twig.extendFilter('strpad', function(value, args) {
    const [targetLength, padString] = args;
    return value.padStart(targetLength, padString);
});

import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import de from 'i18n-iso-countries/langs/de.json'

countries.registerLocale(en)
countries.registerLocale(de)

import { checkRestrictions } from '@/helpers'

import { useSettingsStore, useSetupStore } from '@/stores'
import CmsApi from '@/store/api/cms/cms'

import Error from '@/components/Error';
import SettingField from './Partials/SettingField'
import MicroserviceObjectForm from '@/components/MicroserviceObjectForm'
import GuestMarketRules from './Partials/GuestMarketRules'
import ReturnReasons from './ReturnReasons'

export default {
    name: 'AbonementShop',
    data() {
        return {
            // Multistore: choosing the shared shop this store belongs to. Only
            // reachable while the store has the shop extension and no shop.
            parentShopId: null,
            attachableShops: [],
            loadingAttachableShops: false,
            showInvoiceTemplateGuide: false,
            invoiceNrPreview: undefined,
            showCustomerTemplateGuide: false,
            customerNrPreview: undefined,
            // Anchor target for "Configure reasons" links from elsewhere
            // (PartiallyCancelForm). Renders inside the _returns collapse.
            returnReasonsAnchorId: 'return-reasons-anchor',
        }
    },
    components: {
        MicroserviceObjectForm,
        Error,
        SettingField,
        GuestMarketRules,
        ReturnReasons,
    },
    watch: {
        // Only fetch when the chooser is actually on screen: a store with a shop
        // never needs the list, and this runs on every settings load.
        shop: {
            immediate: true,
            handler() {
                if (this.shop && !_.get(this.form, 'shop')) this.loadAttachableShops();
            },
        },
        invoice_nr_template() {
            this.generateInvoiceNrPreview()
        },
        customer_nr_template() {
            this.generateCustomerNrPreview()
        }
    },
    computed: {
        /**
         * The numbering guide's worked examples, rendered from the SAME template
         * string that is shown above them and against today's date - so neither the
         * example output nor the "if the year is ..." sentence can go stale the way
         * the hardcoded ones did.
         */
        invoiceNrExampleTemplate() {
            return "INV-{{ Y }}-{{ M }}-{{ incr | strpad(6,'0') }}"
        },
        customerNrExampleTemplate() {
            return "C-{{ Y }}-{{ M }}-{{ incr | strpad(6,'0') }}"
        },
        invoiceNrExampleNumber() {
            return '1234'
        },
        customerNrExampleNumber() {
            return '123'
        },
        exampleYear() {
            return format(new Date(), 'yyyy')
        },
        exampleMonthName() {
            // activeDateLocale, not a hardcoded locale: a German month name inside an
            // English sentence is exactly what that helper exists to prevent.
            return format(new Date(), 'MMMM', { locale: activeDateLocale() })
        },
        invoiceNrExample() {
            return this.renderNumberingExample(this.invoiceNrExampleTemplate, this.invoiceNrExampleNumber)
        },
        customerNrExample() {
            return this.renderNumberingExample(this.customerNrExampleTemplate, this.customerNrExampleNumber)
        },
        settings() {
            const settings = _.get(this.form, 'config.template.settings') || []
            return settings.find(el => el.key === 'shop')
        },
        // images() {
        //     return _.get(this.settings, 'images') || []
        // },
        blocks() {
            return _.get(this.settings, 'blocks') || []
        },
        groups() {
            return _.get(this.settings, 'groups') || []
        },

        template: {
            get() {
                return _.get(this.form, 'config.template')
            },
            set(value) {
                useSettingsStore().setFormTemplate(value)
            }
        },
        extensions() {
            return _.get(this.template, 'extensions') || []
        },
        requirements() {
            return _.get(this.template, 'requirements') || []
        },
        cms: {
            get() {
                return true
            },
            set() {
                // TODO
            }
        },
        shop: {
            get() {
                // return !! _.get(this.data, 'shop') && _.get(this.data, 'shop.deleted') !== true

                const extensions = this.form.extensions || []
                return !!extensions.find(extension => extension.key === 'shop')
            },
            set(value) {
                if (value === true) {
                    useSettingsStore().createFormShop()
                    useSettingsStore().addFormExtension({ key: 'shop' })
                } else {
                    // store.commit(`account/form/administrator/deleteShop`, this.index)
                    useSettingsStore().removeFormExtension({ key: 'shop' })
                }
            }
        },
        pos: {
            get() {
                // return !! _.get(this.data, 'shop') && _.get(this.data, 'shop.deleted') !== true

                const extensions = this.form.extensions || []
                return !!extensions.find(extension => extension.key === 'shop')
                    && !!extensions.find(extension => extension.key === 'shop:point-of-sale')
            },
            set(value) {
                if (value === true) {
                    useSettingsStore().addFormExtension({ key: 'shop:point-of-sale' })
                } else {
                    useSettingsStore().removeFormExtension({ key: 'shop:point-of-sale' })
                }
            }
        },
        // Multistore: the shop's identity is the whole group's. The OWNER store edits
        // it directly - it is the shop - while a satellite writes its own override and
        // shows the shop's value as a placeholder. A field left empty keeps coming from
        // the shop, which is what makes the placeholder honest.
        activeCms() {
            return useSetupStore().activeCms;
        },
        writesStoreOverride() {
            return _.get(this.activeCms, 'is_shop_satellite') === true;
        },
        isMultistoreShop() {
            return (_.get(this.activeCms, 'shop.cmses') || []).length > 1;
        },
        // The identity block writes the shop on the owner store and this store's own
        // override on a satellite - so it is only "shared" in the former case.
        shopIdentityIsShared() {
            return !this.writesStoreOverride && this.isMultistoreShop;
        },

        shop_name: {
            get() {
                return this.identityValue('name', 'company_name')
            },
            set(value) {
                this.setIdentity('name', 'company_name', value)
            }
        },
        shop_email: {
            get() {
                return this.identityValue('email', 'email')
            },
            set(value) {
                this.setIdentity('email', 'email', value)
            }
        },
        shop_phone: {
            get() {
                return this.identityValue('phone', 'phone')
            },
            set(value) {
                this.setIdentity('phone', 'phone', value)
            }
        },
        shop_fax: {
            get() {
                return this.identityValue('fax', 'fax')
            },
            set(value) {
                this.setIdentity('fax', 'fax', value)
            }
        },
        shop_bank_name: {
            get() {
                return this.identityValue('bank_name', 'bank_name')
            },
            set(value) {
                this.setIdentity('bank_name', 'bank_name', value)
            }
        },
        shop_iban: {
            get() {
                return this.identityValue('iban', 'iban')
            },
            set(value) {
                this.setIdentity('iban', 'iban', value)
            }
        },
        shop_bic: {
            get() {
                return this.identityValue('bic', 'bic')
            },
            set(value) {
                this.setIdentity('bic', 'bic', value)
            }
        },
        shop_tax_id: {
            get() {
                return this.identityValue('tax_id', 'tax_id')
            },
            set(value) {
                this.setIdentity('tax_id', 'tax_id', value)
            }
        },
        shop_tax_nr: {
            get() {
                return this.identityValue('tax_nr', 'tax_nr')
            },
            set(value) {
                this.setIdentity('tax_nr', 'tax_nr', value)
            }
        },

        newCustomersShowNetPrices: {
            get() {
                return _.get(this.form, 'shop.new_customers_show_net_prices', false);
            },
            set(value) {
                useSettingsStore().setFormShopField('new_customers_show_net_prices', value);
            },
        },

        newCustomersTaxFree: {
            get() {
                return _.get(this.form, 'shop.new_customers_tax_free', false);
            },
            set(value) {
                useSettingsStore().setFormShopField('new_customers_tax_free', value);
            },
        },

        newPurchaseNotificationEnabled: {
            get() {
                return _.get(this.form, 'shop.new_purchase_notification_enabled', true);
            },
            set(value) {
                useSettingsStore().setFormShopField('new_purchase_notification_enabled', value);
            },
        },

        purchaseMailCopiesEnabled: {
            get() {
                return _.get(this.form, 'shop.purchase_mail_copies_enabled', false);
            },
            set(value) {
                useSettingsStore().setFormShopField('purchase_mail_copies_enabled', value);
            },
        },

        purchaseNotificationRecipientEmail: {
            get() {
                return _.get(this.form, 'shop.purchase_confirmation_recipient_email', '') || '';
            },
            set(value) {
                useSettingsStore().setFormShopField('purchase_confirmation_recipient_email', value);
            },
        },

        purchaseNotificationRecipientFallback() {
            return _.get(this.form, 'shop.email', '') || '';
        },

        revocationWindowDays: {
            get() {
                return _.get(this.form, 'shop.revocation_window_days', 14);
            },
            set(value) {
                useSettingsStore().setFormShopField('revocation_window_days', value);
            },
        },

        deliveryTransitDays: {
            get() {
                return _.get(this.form, 'shop.delivery_transit_days', 2);
            },
            set(value) {
                useSettingsStore().setFormShopField('delivery_transit_days', value);
            },
        },

        returnGracePeriodDays: {
            get() {
                return _.get(this.form, 'shop.return_grace_period_days', 14);
            },
            set(value) {
                useSettingsStore().setFormShopField('return_grace_period_days', value);
            },
        },

        returnReminderDaysBefore: {
            get() {
                return _.get(this.form, 'shop.return_reminder_days_before', 3);
            },
            set(value) {
                useSettingsStore().setFormShopField('return_reminder_days_before', value);
            },
        },

        address() {
            let address;
            if (this.addresses && this.addresses.length) address = this.addresses[0]
            return address;
        },
        shop_address() {
            return _.get(this.form, 'shop.address')
        },
        shop_address_street: {
            get() {
                return this.identityValue('address.street', 'address.street')
            },
            set(value) {
                this.setIdentity('address.street', 'address.street', value)
            }
        },
        shop_address_zipcode: {
            get() {
                return this.identityValue('address.zipcode', 'address.zipcode')
            },
            set(value) {
                this.setIdentity('address.zipcode', 'address.zipcode', value)
            }
        },
        shop_address_city: {
            get() {
                return this.identityValue('address.city', 'address.city')
            },
            set(value) {
                this.setIdentity('address.city', 'address.city', value)
            }
        },
        shop_address_country: {
            get() {
                return this.identityValue('address.country', 'address.country')
            },
            set(value) {
                this.setIdentity('address.country', 'address.country', value)
            }
        },
        countries() {
            return countries.getNames('de')
        },
        // The select lists country NAMES, so the inherited code has to be
        // translated before it can stand in as a placeholder.
        shopAddressCountryPlaceholder() {
            const inheritedCountryCode = this.shopIdentityFallback('address.country');
            if (!inheritedCountryCode) return '';

            return this.countries[inheritedCountryCode] || inheritedCountryCode;
        },
        isMultistore() {
            return (_.get(this.form, 'shop.cmses') || []).length > 1;
        },
        // Multistore: number series run per store — the templates and reset
        // settings live on the CMS. The shop values remain a read fallback
        // for stores that were never saved since the migration.
        invoice_nr_template: {
            get() {
                return _.get(this.form, 'invoice_nr_template') || _.get(this.form, 'shop.invoice_nr_template', '')
            },
            set(value) {
                useSettingsStore().setFormCmsField('invoice_nr_template', value)
            }
        },
        customer_nr_template: {
            get() {
                return _.get(this.form, 'customer_nr_template') || _.get(this.form, 'shop.customer_nr_template', '')
            },
            set(value) {
                useSettingsStore().setFormCmsField('customer_nr_template', value)
            }
        },
        resetIncrementNumbersAt: {
            get() {
                let timestamp = _.get(this.form, 'reset_increment_numbers_at');
                if (timestamp == null) timestamp = _.get(this.form, 'shop.reset_increment_numbers_at');
                return timestamp != null ? appFromUnixTime(timestamp) : '';
            },
            set(value) {
                const timestamp = value != null ? appGetUnixTime(value) : null;
                useSettingsStore().setFormCmsField('reset_increment_numbers_at', timestamp)
            }
        },

        new_increment_all_time: {
            get() {
                return _.get(this.form, 'new_increment_all_time') || _.get(this.form, 'shop.new_increment_all_time') || undefined
            },
            set(value) {
                useSettingsStore().setFormCmsField('new_increment_all_time', Math.round(value))
            }
        },
        new_increment_yearly: {
            get() {
                return _.get(this.form, 'new_increment_yearly') || _.get(this.form, 'shop.new_increment_yearly') || undefined
            },
            set(value) {
                useSettingsStore().setFormCmsField('new_increment_yearly', Math.round(value))
            }
        },
        new_increment_monthly: {
            get() {
                return _.get(this.form, 'new_increment_monthly') || _.get(this.form, 'shop.new_increment_monthly') || undefined
            },
            set(value) {
                useSettingsStore().setFormCmsField('new_increment_monthly', Math.round(value))
            }
        },

        form() {
            return useSettingsStore().form;
        },
        settingValues() {
            return useSettingsStore().values;
        },
        options() {
            return useSettingsStore().options;
        },
        uploadEndpoint() {
            return useSetupStore().uploadEndpoint;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        enabledFeatures() {
            return useSetupStore().enabledFeatures;
        },
        alternativeGuestCustomerGroupsFeatureEnabled() {
            if (! (this.enabledFeatures || []).includes('alternative-guest-customer-groups')) return false;

            // Feature is opt-in per CMS via the picker-mode setting. `none`
            // (default) means the admin hasn't activated guest market handling
            // — hide the rules editor.
            const pickerMode = this.settingValues && this.settingValues['guest-market-picker-mode'];
            return pickerMode && pickerMode !== 'none';
        },
        guestMarketGroups() {
            return (this.groups || []).filter(group => {
                const conditions = _.get(group, 'conditions') || [];
                return conditions.some(condition => condition.condition && condition.condition.includes('alternative-guest-customer-groups'));
            });
        },
        otherGroups() {
            return (this.groups || []).filter(group => {
                const conditions = _.get(group, 'conditions') || [];
                return !conditions.some(condition => condition.condition && condition.condition.includes('alternative-guest-customer-groups'));
            });
        },
    },
    methods: {
        /**
         * The shops this store may join. Asked of the server rather than filtered
         * here: CP_AttachableShops applies the same rule the attach itself enforces
         * (same owner, shop has an owner store), so the picker cannot offer
         * something the mutation would refuse.
         */
        loadAttachableShops() {
            if (this.loadingAttachableShops) return;

            this.loadingAttachableShops = true;

            CmsApi.fetchAttachableShops(_.get(this.form, 'id'))
                .then(shops => { this.attachableShops = shops || []; })
                .catch(() => { this.attachableShops = []; })
                .finally(() => { this.loadingAttachableShops = false; });
        },

        /**
         * Flushed by the parent form's Save, the way the communication tab flushes
         * its auto-replies - the tab carries no Save of its own. Throwing aborts
         * that Save, so a refused attach does not look like it succeeded.
         *
         * No reload afterwards: the CMS fragment the Save returns carries `shop`,
         * so replacing the form state is what swaps this chooser for the shop
         * settings.
         */
        async saveTabSettings() {
            if (!this.parentShopId || _.get(this.form, 'shop')) return;

            try {
                await CmsApi.attachCmsToShop(_.get(this.form, 'id'), this.parentShopId);
            } catch (error) {
                this.$notify.error({
                    message: _.get(error, 'graphQLErrors.0.message') || this.$t('Store could not be connected'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });

                throw error;
            }
        },
        /**
         * Placeholder for a numbering field: the counter the next invoice would use.
         *
         * The counters are null on a shop that has never issued one, and calling
         * .toString() on null threw during render - which blanks the ENTIRE settings
         * screen, not just the field. Null-safe here rather than in the template
         * because lodash is not exposed to templates and admin-ui has no optional
         * chaining. Also covers a store with no shop at all, which is the state a
         * satellite is in before it picks a parent.
         */
        numberingPlaceholder(field) {
            const value = _.get(this.form, `shop.purchase_increment_${field}`);

            return value === null || value === undefined ? '1' : value.toString();
        },
        /**
         * Render one of the guide's example templates against today's date, through
         * the same twig engine (and strpad filter) the live previews use - so the
         * example cannot promise something the real generator would not produce.
         */
        renderNumberingExample(templateString, incr) {
            const now = new Date()

            try {
                return twig({ data: templateString }).render({
                    Y: format(now, 'yyyy'),
                    y: format(now, 'yy'),
                    M: format(now, 'MM'),
                    m: format(now, 'M'),
                    D: format(now, 'dd'),
                    d: format(now, 'd'),
                    incr,
                })
            } catch (error) {
                return ''
            }
        },
        // Where this input's value lives in the form model. el-form resolves a
        // form-item's `prop` against the model, so it has to follow the same branch
        // the getter and the setter below already do - a satellite edits its own
        // documentSender.*, the owner edits shop.*. With the wrong path a rule added
        // later would silently never fire.
        identityProp(shopField, senderField) {
            if (this.writesStoreOverride) return `documentSender.${senderField}`;

            return `shop.${shopField}`;
        },
        identityValue(shopField, senderField) {
            if (this.writesStoreOverride) return _.get(this.form, `documentSender.${senderField}`, '') || '';

            return _.get(this.form, `shop.${shopField}`, '') || '';
        },
        setIdentity(shopField, senderField, value) {
            const settingsStore = useSettingsStore();

            if (this.writesStoreOverride) return settingsStore.setFormDocumentSenderField(senderField, value);

            if (shopField.indexOf('address.') === 0) {
                return settingsStore.setFormShopAddressField(shopField.replace('address.', ''), value);
            }

            return settingsStore.setFormShopField(shopField, value);
        },
        // What this store inherits when it leaves the field empty.
        //
        // Resolved by the API, never re-derived here: the shop name is NOT what a
        // satellite falls back to - it trades under its own store name - and a
        // placeholder that promises something else than the document prints is
        // worse than none. The address block keeps reading the shop directly,
        // because an address is inherited whole rather than per field.
        shopIdentityFallback(shopField) {
            if (!this.writesStoreOverride) return '';

            if (shopField.indexOf('address.') === 0) {
                return _.get(this.form, `shop.${shopField}`, '') || '';
            }

            return _.get(this.form, `documentSenderFallback.${shopField}`, '') || '';
        },
        generateInvoiceNrPreview() {
            let preview
            try {
                const template = twig({ data: this.invoice_nr_template })
                const variables = {
                    ID: _.get(this.form, 'shop.purchase_next_id'),
                    uniq: uniqid(),
                    Y: format(new Date, 'yyyy'),
                    y: format(new Date, 'yy'),
                    M: format(new Date, 'MM'),
                    m: format(new Date, 'M'),
                    D: format(new Date, 'dd'),
                    d: format(new Date, 'd'),
                    incr: _.get(this.form, 'shop.purchase_increment_all_time', 1).toString(),
                    incrm: _.get(this.form, 'shop.purchase_increment_monthly', 1).toString(),
                    incry: _.get(this.form, 'shop.purchase_increment_yearly', 1).toString(),
                    incr_shop: _.get(this.form, 'shop.purchase_increment_all_time', 1).toString(),
                    incrm_shop: _.get(this.form, 'shop.purchase_increment_monthly', 1).toString(),
                    incry_shop: _.get(this.form, 'shop.purchase_increment_yearly', 1).toString(),
                }
                if (this.resetIncrementNumbersAt) {
                    if (this.new_increment_all_time) {
                        variables.incr = Math.round(this.new_increment_all_time).toString()
                    }
                    if (this.new_increment_monthly) {
                        variables.incrm = Math.round(this.new_increment_monthly).toString()
                    }
                    if (this.new_increment_yearly) {
                        variables.incry = Math.round(this.new_increment_yearly).toString()
                    }
                }
                preview = template.render(variables)
            } catch (error) {
                console.error(error)
                // this.$notify.error({
                //     title: this.$t('An error occurred!'),
                //     message: error.message,
                //     iconClass: 'fas el-icon-fa-xmark',
                //     position: 'bottom-left',
                //     customClass: 'dark',
                //     duration: 8000
                // });
            }

            this.invoiceNrPreview = preview
        },
        generateCustomerNrPreview() {
            let preview
            try {
                const template = twig({ data: this.customer_nr_template })
                const variables = {
                    ID: _.get(this.form, 'shop.customer_next_id'),
                    uniq: uniqid(),
                    Y: format(new Date, 'yyyy'),
                    y: format(new Date, 'yy'),
                    M: format(new Date, 'MM'),
                    m: format(new Date, 'M'),
                    D: format(new Date, 'dd'),
                    d: format(new Date, 'd'),
                    incr: _.get(this.form, 'shop.customer_increment_all_time', 1).toString(),
                    incrm: _.get(this.form, 'shop.customer_increment_monthly', 1).toString(),
                    incry: _.get(this.form, 'shop.customer_increment_yearly', 1).toString(),
                    incr_shop: _.get(this.form, 'shop.customer_increment_all_time', 1).toString(),
                    incrm_shop: _.get(this.form, 'shop.customer_increment_monthly', 1).toString(),
                    incry_shop: _.get(this.form, 'shop.customer_increment_yearly', 1).toString(),
                }
                preview = template.render(variables)
            } catch (error) {
                console.error(error)
                // this.$notify.error({
                //     title: this.$t('An error occurred!'),
                //     message: error.message,
                //     iconClass: 'fas el-icon-fa-xmark',
                //     position: 'bottom-left',
                //     customClass: 'dark',
                //     duration: 8000
                // });
            }

            this.customerNrPreview = preview
        },

        disabledDate(date) {
            return false
            // return isPast(endOfDay(date));
        },
        checkConditions(setting) {
            let valid = true

            // eslint-disable-next-line no-unused-vars
            const settings = this.settingValues;
            // eslint-disable-next-line no-unused-vars
            const enabledExtensions = this.enabledExtensions;
            // eslint-disable-next-line no-unused-vars
            const enabledFeatures = this.enabledFeatures;
            try {
                const conditions = setting.conditions
                if (conditions && conditions.length) {
                    conditions.forEach(o => {
                        if (valid === false) return

                        const { type, condition } = o
                        switch (type) {
                            case 'eval':
                                valid = eval(condition)
                                break;

                            default:
                                valid = false
                        }
                    })
                }
            } catch (error) {
                // eslint-disable-next-line
                console.error(error);
                valid = false
            }

            return valid
        },
        addShopAddress() {
            useSettingsStore().addFormShopAddress()
        },
        removeShopAddress() {
            useSettingsStore().removeFormShopAddress()
        },
        extensionAvailable(extension) {
            return this.extensions.includes(extension)
        },
        checkRestrictions,
    },
    mounted() {
        this.generateInvoiceNrPreview()
        this.generateCustomerNrPreview()
    }
}
</script>

<style lang="sass" scoped>
// The picker came from the wizard, where it lived in a narrow step column. Dropped
// into a full-width settings card it stretched a one-value select across ~1900px and
// pushed the two summary columns so far apart they read as unrelated lists. Hold it
// to a readable measure instead; the component itself stays width-agnostic.
.shop-attach
    .help
        display: block
        margin-top: 4px
        font-size: 12px
        color: var(--color-text-muted, #909399)
        line-height: 1.4

.shared-scope
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 8px
    margin-bottom: 16px

.shared-scope__hint
    flex: 1 1 240px
    min-width: 0
    font-size: 12px
    line-height: 1.4
    color: #909399
.address.card
    margin-bottom: 1rem

.row
    display: flex
    margin-bottom: 12px
    & > span.flex
        width: 100%
        display: flex
        flex-wrap: wrap
        row-gap: 20px

.setting-field
    margin-right: 20px
    &.inline-switch,
    &.no-margin .el-form-item
        margin-bottom: 0

.invoice-template-guide,
.invoice-template-preview,
.customer-template-guide,
.customer-template-preview
    h2
        margin-top: 0
        font-size: 1.2rem
    h3
        font-size: 1rem
    code
        display: inline-block
        font-size: 0.9rem
        font-weight: bold
        background-color: rgba(0, 0, 0, 0.1)
        // padding: 0.2rem 0.5rem
        padding: 1px 4px
        border-radius: 0.2rem
        // margin: 0.5rem 0
        margin: 2px 0

.code-font::v-deep input
    font-family: 'Fira Code', 'Courier New', Courier, monospace
    font-weight: 600

.invoice-template-preview,
.customer-template-preview
    h2
        margin: 0
.error
    display: flex
    justify-content: center
    min-height: 200px

// Returns collapse — predictable 2-column grid so the four time-window
// inputs align in a tidy 2x2 instead of as four floating islands inside
// flex-rows. Each cell's input fills the column (is-full-width) and the
// help text stacks underneath without leaking into the adjacent cell.
.returns-grid
    display: grid
    grid-template-columns: repeat(2, minmax(0, 1fr))
    gap: 8px 32px
    .returns-field
        margin-bottom: 8px
        // Element UI's el-input-number renders an inline-flex bar by
        // default — opt into full-width so it occupies the grid cell.
        ::v-deep .el-input-number.is-full-width
            width: 100%
    .help
        display: block
        margin-top: 4px
        font-size: 12px
        color: var(--color-text-muted, #909399)
        line-height: 1.4

.return-reasons-block
    margin-top: 24px
    padding-top: 16px
    border-top: 1px solid var(--border-color, #ebeef5)
</style>
